import { Alert, Button, Grid, Group, Modal, SegmentedControl, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconChevronLeft,
  IconChevronRight,
  IconCopy,
  IconDeviceFloppy,
  IconPencil,
  IconPlus,
  IconX,
} from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import CampaignFormInner from '../../components/Campaigns/CampaignFormInner';
import prettifiedLabel from '../../constants/labelPrettifier';
import useFetch from '../../helpers/useFetch';
import CrossDomainCopy from '../CrossDomainCopy';
import EmailsDetailsForm from '../Emails/EmailsDetailsForm';
import EmailsNewForm from '../Emails/EmailsNewForm';
import ErrorMessage from '../ErrorMessage';
import FacebookAdsDetailsForm from '../Facebook/Ads/FacebookAdsDetailsForm';
import FacebookAdsNewForm from '../Facebook/Ads/FacebookAdsNewForm';
import GoogleAdsDetailsForm from '../Google/Ads/GoogleAdDetailsFrom';
import GoogleAdsNewForm from '../Google/Ads/GoogleAdNewForm';
import LockableButton from '../Lockable/LockableButton';
import ConfirmationModal from '../Modals/ConfirmationModal';
import PreviewHTML from '../PreviewHTML';
import SearchableCombobox from '../SearchableCombobox';

const CampaignForm = ({ campaign: passedCampaign, deleteCampaign, saveCampaign, close, loading }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [campaign, setCampaign] = useState(passedCampaign);
  const [showNewMessageModal, setShowNewEmailModal] = useState(false);
  const [showEditMessageModal, setShowEditMessageModal] = useState(false);
  const [error, setError] = useState();
  const [emails, setEmails] = useState();
  const [facebookAds, setFacebookAds] = useState();
  const [email, setEmail] = useState();
  const [googleAds, setGoogleAds] = useState([]);

  const node = campaign?.flow?.nodes?.find((node) => node.id !== 'start');

  const tabs = ['content', 'audience', passedCampaign.mode !== 'custom' ? 'launchConfiguration' : null, 'goal'].filter(
    (t) => !!t
  );
  const [tab, setTab] = useState(tabs[0]);
  const [validation, setValidation] = useState(true);
  const [hasAssetMinimumErrors, setHasAssetMinimumErrors] = useState(false);
  const fetch = useFetch();
  const { accountId, domainId } = useParams();
  const googleAccount = useSelector((state) => state.google.account);

  const refreshEmail = useCallback(() => {
    if (node.data.custom.key === 'sendEmail' && node.data.custom.values.id) {
      fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/emails/${node.data.custom.values.id}`)
        .then(({ email }) => {
          setEmail(email);
        })
        .catch(setError);
    } else {
      setEmail(undefined);
    }
  }, [accountId, domainId, fetch, node.data.custom.key, node.data.custom.values.id]);

  const loadEmails = useCallback(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/emails`)
      .then(({ emails }) => {
        setEmails(emails);
      })
      .catch(setError);
  }, [accountId, domainId, fetch]);

  const loadFacebookAds = useCallback(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/facebookAds`)
      .then(({ facebookAds }) => {
        setFacebookAds(facebookAds);
      })
      .catch(setError);
  }, [accountId, domainId, fetch]);

  const loadGoogleAds = useCallback(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/googleAds`)
      .then(({ googleAds }) => {
        const filteredGoogleAds = googleAds.filter((ad) => {
          return googleAccount?.adAccountId && ad.resourceName?.includes(googleAccount.adAccountId);
        });
        setGoogleAds(filteredGoogleAds);
      })
      .catch(setError);
  }, [accountId, domainId, fetch, googleAccount?.adAccountId]);

  useEffect(() => {
    loadEmails();
    loadFacebookAds();
    loadGoogleAds();
  }, [loadEmails, loadFacebookAds, loadGoogleAds]);

  const updateSelectedMessage = (id) => {
    const flow = JSON.parse(JSON.stringify(campaign.flow));
    flow.nodes.find((n) => n.id === node.id).data.custom.values.id = id;
    setCampaign({ ...campaign, flow });
  };

  useEffect(() => {
    refreshEmail();
  }, [refreshEmail]);

  if (campaign === undefined || emails === undefined || facebookAds === undefined) {
    return '';
  }

  const isCampaignEditLocked =
    (campaign.mode !== 'oneOff' && campaign.running) ||
    (campaign.mode === 'oneOff' && (campaign.running || campaign.finishedAt || campaign.aborted)) ||
    campaign.googleAssetGroup?.campaignResourceName;

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  let selectOptions = [];
  let type;
  let newForm;
  let detailsForm;
  let segmentScope;

  if (node.data.custom.key === 'sendFacebookAd') {
    selectOptions = facebookAds.map((e) => ({
      label: e.name,
      value: e.id.toString(),
    }));
    type = 'Facebook Ad';
    segmentScope = 'facebook';
    newForm = (
      <FacebookAdsNewForm
        from={node.data.custom.values.id}
        onCreate={(message) => {
          loadFacebookAds();
          updateSelectedMessage(message.id);
          setShowNewEmailModal(false);
        }}
        onBack={() => setShowNewEmailModal(false)}
      />
    );
    detailsForm = (
      <FacebookAdsDetailsForm
        onUpdate={() => {
          loadFacebookAds();
          setShowEditMessageModal(false);
        }}
        onBack={() => {
          setShowEditMessageModal(false);
        }}
        facebookAd={facebookAds.find((e) => e.id === node.data.custom.values.id)}
      />
    );
  } else if (node.data.custom.key === 'sendEmail') {
    selectOptions = emails.map((e) => ({
      label: `${e.name} (subject: ${e.subject})`,
      value: e.id.toString(),
    }));
    type = 'Email';
    segmentScope = 'behavior';
    newForm = (
      <EmailsNewForm
        from={node.data.custom.values.id}
        editor="stripo"
        onCreate={(message) => {
          loadEmails();
          updateSelectedMessage(message.id);
          setShowNewEmailModal(false);
        }}
        onBack={() => setShowNewEmailModal(false)}
      />
    );
    detailsForm = (
      <EmailsDetailsForm
        onUpdate={(redirect) => {
          loadEmails();
          refreshEmail();
          if (redirect) {
            setShowEditMessageModal(false);
          }
        }}
        onBack={() => {
          setShowEditMessageModal(false);
        }}
        email={email}
      />
    );
  } else if (node.data.custom.key === 'sendGoogleAd') {
    selectOptions = googleAds.map((e) => ({
      label: e.name,
      value: e.id.toString(),
    }));
    type = 'Google Ad';
    segmentScope = 'google';
    tabs.push('assetGroupUrlConfiguration');
    newForm = (
      <GoogleAdsNewForm
        from={node.data.custom.values.id}
        onCreate={(message) => {
          loadGoogleAds();
          updateSelectedMessage(message.id);
          setShowNewEmailModal(false);
        }}
        onBack={() => setShowNewEmailModal(false)}
      />
    );
    detailsForm = (
      <GoogleAdsDetailsForm
        onUpdate={() => {
          loadGoogleAds();
          setShowEditMessageModal(false);
        }}
        onBack={() => {
          setShowEditMessageModal(false);
        }}
        googleAd={googleAds.find((e) => e.id === node.data.custom.values.id)}
      />
    );
  }

  const handleChange = (e) => {
    setCampaign((prevCampaign) => {
      let change = {};
      let changeInner = change;
      let campaignInner = { ...prevCampaign };

      const nameSplit = e.target.name.split('.');
      let i = 0;
      let key;
      for (;;) {
        key = nameSplit[i];
        i++;

        if (i === nameSplit.length) {
          changeInner[key] = e.target.value;
          break;
        } else {
          changeInner[key] = { ...campaignInner[key] };
          changeInner = changeInner[key];
          campaignInner = campaignInner[key] || {};
        }
      }

      return { ...prevCampaign, ...change };
    });
  };

  let deleteButton;
  if (deleteCampaign) {
    deleteButton = (
      <LockableButton
        restrictionKey={`${campaign.mode}CampaignsDelete`}
        color="red"
        onClick={() => setConfirmDeleteOpen(true)}
      >
        <IconX size={16} /> Delete
      </LockableButton>
    );
  }

  let duplicateButton;
  if (campaign.id && campaign.mode !== 'custom') {
    duplicateButton = (
      <CrossDomainCopy
        resourceType="Campaign"
        resourceId={campaign.id}
        basePath="campaigns"
        restrictionKey={`${campaign.mode}CampaignsCreate`}
        fallbackAction={() => {
          navigate(
            location.pathname.replace(`/campaigns/${campaign.id}/details`, `/campaigns/new?from=${campaign.id}`)
          );
        }}
      />
    );
  }

  const goalNames = (campaign.goals || []).map((g) => g.name?.trim());
  const hasEmptyGoalName = goalNames.some((name) => !name);
  const filledGoalNames = goalNames.filter(Boolean);
  const hasDuplicateGoalNames = new Set(filledGoalNames).size !== filledGoalNames.length;

  const saveDisabled =
    !campaign ||
    !campaign.name ||
    campaign.mode === 'custom' ||
    (campaign.mode === 'triggered' && !campaign.trigger) ||
    (campaign.mode !== 'triggered' && campaign.trigger?.type !== 'event' && !campaign.segmentId) ||
    !node.data.custom.values?.id ||
    (campaign.googleAssetGroup?.pathOne === '' && campaign.googleAssetGroup?.pathTwo !== '') ||
    campaign.googleAssetGroup?.pathOne?.length > 15 ||
    campaign.googleAssetGroup?.pathTwo?.length > 15 ||
    (campaign.mode === 'continuous' &&
      campaign.reentrable &&
      campaign.reentryCooldownInDays < 1 &&
      !(campaign.reentryCooldownInHours >= 3 && campaign.reentryCooldownInHours <= 24)) ||
    (node.data.custom.key === 'sendGoogleAd' && hasAssetMinimumErrors) ||
    hasEmptyGoalName ||
    hasDuplicateGoalNames;

  return (
    <>
      <form
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <CampaignFormInner
          tab={tab}
          setTab={setTab}
          campaign={campaign}
          handleChange={(e) => handleChange(e)}
          segmentScope={segmentScope}
          onAssetValidationChange={setHasAssetMinimumErrors}
          mainContent={
            <>
              {(campaign.mode === 'oneOff' || campaign.mode === 'continuous') && (
                <Grid mb="md">
                  <Grid.Col span={{ base: 12, sm: 2 }} style={{ display: 'flex', alignItems: 'center' }}>
                    <label>Message</label>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 10 }}>
                    <SegmentedControl
                      disabled={!!isCampaignEditLocked}
                      value={node.data.custom.key ?? ''}
                      onChange={(value) => {
                        const flow = JSON.parse(JSON.stringify(campaign.flow));
                        const modifiedNode = flow.nodes.find((n) => n.id === node.id);
                        modifiedNode.data.custom.key = value;
                        modifiedNode.data.custom.values.id = undefined;
                        modifiedNode.data.label = prettifiedLabel(value);
                        setCampaign({ ...campaign, flow, segmentId: null });
                      }}
                      // Google Ads are only offered for continuous campaigns. Facebook Ads only for
                      // one-off (continuous Facebook is workflow-only). Existing one-off Google
                      // campaigns keep Google Ad in the list so the picker stays consistent.
                      data={
                        campaign.mode === 'oneOff'
                          ? [
                              { label: 'Email', value: 'sendEmail' },
                              { label: 'Facebook Ad', value: 'sendFacebookAd' },
                              ...(node.data.custom.key === 'sendGoogleAd'
                                ? [{ label: 'Google Ad', value: 'sendGoogleAd' }]
                                : []),
                            ]
                          : [
                              { label: 'Email', value: 'sendEmail' },
                              { label: 'Google Ad', value: 'sendGoogleAd' },
                            ]
                      }
                    />
                  </Grid.Col>
                </Grid>
              )}
              <Grid mb="md">
                <Grid.Col span={{ base: 12, sm: 2 }} style={{ display: 'flex', alignItems: 'center' }}>
                  <label>{type}</label>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 10 }}>
                  <Grid>
                    <Grid.Col span={{ base: 12, md: 8 }} mb="sm">
                      <SearchableCombobox
                        options={selectOptions}
                        value={node.data.custom.values.id?.toString()}
                        onChange={(value) => {
                          const flow = JSON.parse(JSON.stringify(campaign.flow));
                          flow.nodes.find((n) => n.id === node.id).data.custom.values.id = parseInt(value);
                          setCampaign({ ...campaign, flow });
                        }}
                        placeholder={`(Select ${type})`}
                        searchPlaceholder={`Search ${type?.toLowerCase()}s...`}
                        emptyMessage={`No ${type?.toLowerCase()}s found`}
                        disabled={isCampaignEditLocked}
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, md: 2 }}>
                      <Button
                        fullWidth
                        onClick={() => setShowEditMessageModal(true)}
                        disabled={!node.data.custom.values.id || isCampaignEditLocked}
                      >
                        <IconPencil size={16} /> Edit
                      </Button>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, md: 2 }}>
                      <Button
                        fullWidth
                        data-tour="newEmail"
                        onClick={() => setShowNewEmailModal(true)}
                        disabled={isCampaignEditLocked}
                      >
                        {node.data.custom.values.id ? <IconCopy size={16} /> : <IconPlus size={16} />}{' '}
                        {node.data.custom.values.id ? 'Duplicate & Edit' : 'New'}
                      </Button>
                    </Grid.Col>
                  </Grid>
                </Grid.Col>
              </Grid>

              {node.data.custom.key === 'sendEmail' && <PreviewHTML htmlPreview={email?.html} />}
            </>
          }
          isCampaignEditLocked={isCampaignEditLocked}
        />

        {!validation ? (
          <Alert color="red" style={{ margin: '20px 0 0 0', textAlign: 'center' }}>
            <div>
              We have detected errors in your HTML and we recommend you fix them to avoid display issues. You may still
              use this content but it will be at your own responsibility.
            </div>
            <Button onClick={() => setValidation(true)} mt="md">
              I understand and want to save the email.
            </Button>
          </Alert>
        ) : null}
        <Group justify="flex-end" mt="md" gap="xs" py="md" align="center">
          {node.data.custom.key === 'sendGoogleAd' && hasAssetMinimumErrors && (
            <Text size="xs" c="red">
              Resolve asset issues to save
            </Text>
          )}
          <Button
            variant="default"
            onClick={() => {
              if (tabs.indexOf(tab) === 0) {
                return close();
              }

              setTab(tabs[tabs.indexOf(tab) - 1]);
              window.scrollTo(0, 0);
            }}
          >
            {' '}
            <IconChevronLeft /> Back
          </Button>
          {deleteButton}
          {duplicateButton}
          {campaign.id || tabs.indexOf(tab) === tabs.length - 1 ? (
            <>
              {campaign.id ? (
                <LockableButton
                  color="cyan"
                  onClick={() =>
                    saveCampaign(campaign, () => {
                      notifications.show({
                        title: 'Campaign saved',
                        message: 'Your changes have been saved successfully.',
                        color: 'success',
                      });
                    })
                  }
                  restrictionKey={`${campaign.mode}Campaigns${campaign.id ? 'Update' : 'Create'}`}
                  disabled={saveDisabled || loading}
                >
                  <IconDeviceFloppy size={16} /> Save
                </LockableButton>
              ) : null}
              <LockableButton
                variant="primary"
                onClick={() => saveCampaign(campaign, close)}
                restrictionKey={`${campaign.mode}Campaigns${campaign.id ? 'Update' : 'Create'}`}
                disabled={saveDisabled || loading || !validation}
                data-tour="saveBtn"
              >
                <IconDeviceFloppy size={16} /> {loading ? 'Saving...' : 'Save & close'}
              </LockableButton>
            </>
          ) : (
            <Button
              onClick={() => {
                setTab(tabs[tabs.indexOf(tab) + 1]);
                window.scrollTo(0, 0);
              }}
              disabled={!validation}
              data-tour="nextButtonCampaign"
            >
              Next <IconChevronRight size={16} />
            </Button>
          )}
          <ConfirmationModal
            show={confirmDeleteOpen}
            close={() => setConfirmDeleteOpen(false)}
            confirm={(cb) => deleteCampaign(cb)}
            message="This action cannot be undone!"
          />
        </Group>
      </form>

      <Modal
        opened={showNewMessageModal}
        onClose={() => setShowNewEmailModal(false)}
        size="full"
        styles={{ content: { width: '100%', maxWidth: '100%' } }}
        title={`New ${type}`}
      >
        {newForm}
      </Modal>

      <Modal
        opened={showEditMessageModal}
        onClose={() => setShowEditMessageModal(false)}
        size="full"
        styles={{ content: { width: '100%', maxWidth: '100%' } }}
        title={`Edit ${type}`}
      >
        {detailsForm}
      </Modal>
    </>
  );
};

export default CampaignForm;
